import {
    ListItem,
    ListItemIcon,
    ListItemText,
    Tooltip,
} from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/styles";
import classnames from "classnames";
import * as React from "react";
import { Link } from "react-router-dom";

import { baseColors } from "../../";
import { useRouter } from "../hooks";
import { RelodeTheme } from "../styles";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            menuItemIcon: {
                marginTop: theme.spacing(0.5),
                marginBottom: theme.spacing(0.5),
                minWidth: theme.spacing(4),
                "& svg": {
                    color: baseColors.navy,
                },
            },
            menuItemText: {
                textTransform: "lowercase",
                whiteSpace: "nowrap",
                display: "flex",
                flexDirection: "column",
                opacity: 1,
                transition: theme.transitions.create(["opacity"], {
                    easing: theme.transitions.easing.easeOut,
                    duration: theme.transitions.duration.enteringScreen,
                }),
            },
            menuItemTextClosed: {
                opacity: 0,
                transition: theme.transitions.create(["opacity"], {
                    easing: theme.transitions.easing.easeIn,
                    duration: theme.transitions.duration.leavingScreen,
                }),
            },
            tooltipText: {
                maxWidth: "none",
            },
        }),
    { name: "AppMenuItem" }
);

export interface Props {
    id?: string;
    className?: string;
    label: string;
    tooltip: string;
    subheading?: string;
    icon: React.ReactElement<any>;
    route?: string;
    action?: () => void;
    isOpen?: boolean;
    type: "menu" | "system";
    onClick?: (item: { id: string; route: string }) => void;
}

export const AppMenuItem = (props: Props) => {
    const {
        id,
        className,
        label,
        tooltip,
        icon,
        isOpen = true,
        route,
        action,
        onClick = (item: { id: string; route: string }) => {
            /* noop */
        },
    } = props;

    const classes = useStyles({});
    const { location } = useRouter();
    const [selected, setSelected] = React.useState(false);

    React.useEffect(() => {
        if (location.pathname === undefined || route === undefined) return;
        const rootPath = location.pathname.split("/")[1];
        // JLC: use regex here to extract just the path
        const rootRoute = route.match(/^\/([^?/]+)/)[1];
        setSelected(rootPath === rootRoute);
    }, [location.pathname, route]);

    const menuItemTextClasses = classnames({
        [classes.menuItemText]: true,
        [classes.menuItemTextClosed]: !isOpen,
    });

    // https://v4.mui.com/guides/composition/#react-router
    const CustomLink = React.useMemo(
        () =>
            React.forwardRef((props, ref: React.Ref<HTMLAnchorElement>) => (
                <Link ref={ref} to={route} {...props} />
            )),
        [route]
    );

    const handleOnClick = React.useCallback(() => {
        onClick({ id, route });
        if (!!action) {
            action();
        }
    }, [action, onClick, route, id]);

    return (
        <Tooltip
            placement="bottom-start"
            className={className}
            title={tooltip}
            classes={{ tooltip: classes.tooltipText }}
        >
            <ListItem
                data-cy={`${id}-MenuItem`}
                id={`${id}-MenuItem`}
                onClick={!!route ? null : handleOnClick}
                selected={selected}
                component={!!route ? CustomLink : "div"}
                button
            >
                <ListItemIcon className={classes.menuItemIcon}>
                    {icon}
                </ListItemIcon>
                <ListItemText
                    className={menuItemTextClasses}
                    primary={label}
                    primaryTypographyProps={{ color: "primary" }}
                />
            </ListItem>
        </Tooltip>
    );
};
